import React, { useMemo } from 'react';
import { MemberRegistrationForm } from '../../types';
import { getMembershipLabel } from '../../utils/membership';
import { registrationAmountDollars } from '../../utils/registrationPrice';
import { formatClubDate } from '../../utils/clubTime';

/** Competition entry fee — $10 flat per entrant (junior and senior alike). */
export const COMP_ENTRY_FEE = 10;

interface CompEvent {
  id: string;
  title?: string;
  eventDate?: string;
}

interface CompEntryStepProps {
  formData: MemberRegistrationForm;
  updateFormData: (updates: Partial<MemberRegistrationForm>) => void;
  compEvent: CompEvent;
  onNext: () => void;
  onBack: () => void;
}

const CompEntryStep: React.FC<CompEntryStepProps> = ({
  formData,
  updateFormData,
  compEvent,
  onNext,
  onBack,
}) => {
  // Family members with at least a first name are selectable entrants.
  const namedFamily = useMemo(
    () => (formData.familyMembers || []).filter((fm) => (fm.firstName || '').trim().length > 0),
    [formData.familyMembers]
  );

  const flaggedCount =
    (formData.signupCompFishing ? 1 : 0) + namedFamily.filter((fm) => fm.fishingToday).length;

  const membershipFee = registrationAmountDollars(formData);
  const compFee = flaggedCount * COMP_ENTRY_FEE;
  const total = membershipFee + compFee;

  const togglePrimary = () => updateFormData({ signupCompFishing: !formData.signupCompFishing });

  const toggleFamily = (index: number) => {
    const next = (formData.familyMembers || []).map((fm, i) =>
      i === index ? { ...fm, fishingToday: !fm.fishingToday } : fm
    );
    updateFormData({ familyMembers: next });
  };

  const eventDateLabel = compEvent.eventDate
    ? formatClubDate(compEvent.eventDate, {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
      })
    : '';

  const rowClass =
    'flex items-center gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:border-fishing-blue';

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Fishing in today's competition?</h2>
        <p className="mt-1 text-sm text-gray-600">
          There's a competition on today — <span className="font-semibold">{compEvent.title}</span>
          {eventDateLabel ? ` (${eventDateLabel})` : ''}. Select who's fishing to enter at sign-up.
          It's optional — leave everyone unchecked to skip.
        </p>
      </div>

      <div className="space-y-2">
        <p className="text-sm font-semibold text-gray-900">Who's fishing today?</p>

        <label className={rowClass}>
          <input
            type="checkbox"
            checked={!!formData.signupCompFishing}
            onChange={togglePrimary}
            className="h-4 w-4"
          />
          <span className="text-gray-800">
            {formData.firstName || 'You'} {formData.lastName || ''}{' '}
            <span className="text-gray-400 text-sm">(you)</span>
          </span>
        </label>

        {namedFamily.map((fm) => {
          // Index into the original array so the toggle updates the right entry.
          const realIndex = (formData.familyMembers || []).indexOf(fm);
          return (
            <label key={realIndex} className={rowClass}>
              <input
                type="checkbox"
                checked={!!fm.fishingToday}
                onChange={() => toggleFamily(realIndex)}
                className="h-4 w-4"
              />
              <span className="text-gray-800">
                {fm.firstName} {fm.lastName || ''}
              </span>
            </label>
          );
        })}
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-1 text-sm">
        <div className="flex justify-between">
          <span className="text-gray-600">{getMembershipLabel(formData.membershipType)}</span>
          <span className="text-gray-900">${membershipFee}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-600">
            Competition entry ({flaggedCount} × ${COMP_ENTRY_FEE})
          </span>
          <span className="text-gray-900">${compFee}</span>
        </div>
        <div className="flex justify-between border-t border-gray-200 pt-1 font-semibold">
          <span className="text-gray-900">Total payable on the day</span>
          <span className="text-fishing-blue">${total}</span>
        </div>
        <p className="text-xs text-gray-500 pt-1">
          Payment is made in person on the day — this is just the total to expect.
        </p>
      </div>

      <div className="flex justify-between">
        <button
          type="button"
          onClick={onBack}
          className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
        >
          Back
        </button>
        <button
          type="button"
          onClick={onNext}
          className="px-6 py-2 bg-fishing-blue text-white rounded-md font-medium hover:bg-blue-700"
        >
          Continue
        </button>
      </div>
    </div>
  );
};

export default CompEntryStep;
